<template>

	<div ref="stepper-container" class="bottom-sheet-stepper">
		<div
			v-for="(step, index) in steps"
			 class="stepper-step"
			 :class="{
				'no-pointer': activeDropdown === index,
				failed: step.slotName === 'products-registry' && funnelStore.errorMenuUpload,
				loading: step.slotName === 'products-registry' && funnelStore.pollingMenuUpload,
				active: progress === index + 1 || (!progress && index === 0),
				done: (progress !== null && progress > index + 1)
			}
		">
			<div class="stepper-step-header" @click="clickDropdownHeader(index)">
				<div class="step-circle">
					<div class="step-circle-inner">
						<div v-if="step.slotName === 'products-registry' && funnelStore.pollingMenuUpload" class="circle-check ai-circle-icon" v-html="getIcon('iconAi')"></div>
						<div v-else-if="step.slotName === 'products-registry' && funnelStore.errorMenuUpload" class="circle-check warn-icon" v-html="getIcon('iconWarning')"></div>
						<div v-else-if="!isLastStep(index)" class="circle-check" v-html="getIcon('iconV')"></div>
						<div v-else-if="isLastStep(index)" class="flag-icon" v-html="getIcon('raceFlag')"></div>
					</div>
				</div>
				<div class="step-title">
					<h3 :class="{underline: step.slotName === 'products-registry' && funnelStore.completedMenuUpload === Number(userStore?.currBusinessId) && !funnelStore.errorMenuUpload && !funnelStore.pollingMenuUpload}">{{ step.title }}</h3>
					<div v-if="step.slotName === 'products-registry' && funnelStore.pollingMenuUpload" class="col-12 pt-10">
						<AppProgressBar :indeterminate="(funnelStore?.aiProgress || 0) < 2" :progress="funnelStore?.aiProgress || 0" />
						<p v-if="(funnelStore?.aiProgress || 0) > 1" class="step-subtitle text-align-center">{{ funnelStore?.aiProgress }}%</p>
					</div>
					<div v-else-if="step.slotName === 'products-registry' && funnelStore.errorMenuUpload" class="row centered flex-start pt-2">
						<p class="step-subtitle">{{ getString('error_loading_menu', [userStore.menuWord]) }}</p>
					</div>
				</div>
			</div>
			<div class="stepper-step-content">
				<div v-show="showStepLine(index)" class="stepper-step-line-container">
					<div class="stepper-step-line"></div>
				</div>
				<div class="stepper-step-dropdown-container">
					<AppDropdown square-corner white :collapsed="activeDropdown !== index">
						<slot :name="step.slotName" :item="step">
							<div class="col-12 centered">
								<h4>{{ step.title }}</h4>
							</div>
						</slot>
					</AppDropdown>
				</div>
			</div>
		</div>
		<div class="preview-container" v-if="couldShowPreview">
			<MenuPreview always-mobile />
		</div>
	</div>

</template>

<script>
import AppDropdown from "@/shared/components/AppDropdown.vue";
import AppProgressBar from "@/shared/components/AppProgressBar.vue";
import AppButton from "@/shared/components/AppButton.vue";
import FunnelVideoCard from "@/console-new/components/shared/FunnelVideoCard.vue";
import {useSvgStore, useLanguageStore} from "@/shared/stores";
import {useFunnelStore, useUserStore} from "@/console-new/stores";
import MenuPreview from "@/console-new/components/config/menu/MenuPreview.vue";
import {computed} from "vue";


export default {
	emits: ["go-to-menu"],
	setup() {
		let svgStore = useSvgStore();
		let funnelStore = useFunnelStore();
		let userStore = useUserStore();
		let languageStore = useLanguageStore();
		return {funnelStore, getString: languageStore.getString, userStore, getIcon: svgStore.get};
	},
	name: "FunnelStepsBottomSheetStepper",
	components: {MenuPreview, AppDropdown, AppProgressBar, AppButton, FunnelVideoCard},
	provide() {
		return {
			providedAction: computed(() => {
				return {
					showMobilePreview: this.showMobilePreview,
					toggleMobilePreview: this.toggleMobilePreview,
				}
			})
		}
	},
	data() {
		return {
			activeDropdown: null,
			scrollTimeout: null,
			showMobilePreview: false,
			couldShowPreview: false,
		}
	},
	props: {
		steps: {
			type: Array,
			default: () => [],
		},
		progress: {
			type: Number,
			default: 1
		}
	},
	methods: {
		toggleMobilePreview(newValue = !this.showMobilePreview) {
			this.showMobilePreview = newValue;
			if(!this.showMobilePreview) {
				localStorage.removeItem('menu-upload-complete');
				this.funnelStore.completedMenuUpload = null;
			}
		},
		isLastStep(index) {
			return index === (this.steps?.length || 0) - 1;
		},
		showStepLine(index) {
			let lastIndex = (this.steps?.length || 0) - 1;
			return lastIndex !== index || (this.progress === index + 1 && this.activeDropdown === index);
		},
		clickDropdownHeader(index) {
			let step = this.steps?.[index];
			if (step?.slotName === 'products-registry' && this.funnelStore.completedMenuUpload === Number(this.userStore.currBusinessId) && !this.funnelStore.errorMenuUpload && !this.funnelStore.pollingMenuUpload) {
				this.toggleMobilePreview(true);
				return;
			}
			if(this.activeDropdown === index) {
				return;
			}
			if(this.activeDropdown === null) {
				this.activeDropdown = index;
			} else if(index < this.progress) {
				this.activeDropdown = index;
			}
		},
		resetDropdown() {
			if(this.progress) {
				this.activeDropdown = this.progress - 1;
			}
		},
		scrollToOpenDropdown() {
			if (this.activeDropdown >= 0) {
				const dropdown = this.$el?.querySelectorAll('.stepper-step')?.[this.activeDropdown];
				const container = this.$refs?.['stepper-container']?.parentElement?.parentElement;
				if (dropdown && container) {
					const topDistance = dropdown?.getBoundingClientRect()?.top || 0;
					const containerTop = container?.getBoundingClientRect()?.top || 0;
					const offset = topDistance - containerTop - 20; // Adjust the offset to ensure the title is visible
					const finalOffset = this.activeDropdown === 0 ? 0 : container.scrollTop + offset;
					container.scrollTo({ top: finalOffset, behavior: 'smooth' });
				}
			}
		},
	},
	created() {
		this.resetDropdown();
		this.couldShowPreview = this.funnelStore.completedMenuUpload;
	},
	watch: {
		progress() {
			this.resetDropdown();
		},
		activeDropdown(newVal, oldVal) {
			if(newVal !== oldVal && newVal >= 0) {
				clearTimeout(this.scrollTimeout);
				this.scrollTimeout = setTimeout(this.scrollToOpenDropdown, 500);
			}

		},
		'funnelStore.completedMenuUpload'(newVal) {
			if(newVal) {
				this.couldShowPreview = true;
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.bottom-sheet-stepper {
	margin-top: 20px;

	.stepper-step {

		.stepper-step-header {
			@include centered;
			justify-content: flex-start;
			gap: 12px;
			color: var(--color-alpha-3);
		}
		.stepper-step-content {
			@include centered;
			justify-content: flex-start;
			align-items: stretch;
			gap: 12px;
			padding: 10px 0 12px 0;

			.stepper-step-line-container {
				@include centered;
				justify-content: center;
				width: 35px;

				.stepper-step-line {
					border-radius: 4px;
					width: 6px;
					height: 100%;
					min-height: 20px;
					background-color: var(--color-alpha-2);
					transition: background-color .4s ease;
				}
			}
			.stepper-step-dropdown-container {
				@include centered;
				align-items: flex-start;
				width: calc(100% - 50px);
				padding: 8px 0;

				:deep(.dropdown-content) {
					padding: 0;
					margin-top: 0;
				}
			}
		}

		.step-title {
			@include noSelect;
			max-width: calc(100% - 50px);

			h3.underline {
				text-decoration: underline;
				text-underline-offset: 2px;
				cursor: pointer;
			}

			.step-subtitle {
				font-size: 14px;
				line-height: 14px;
			}
		}
		.step-circle {
			@include centered;
			width: 35px;
			height: 35px;
			border-radius: 50%;
			background-color: var(--color-alpha-2);
			border: 1px solid var(--color-alpha-2);
			transition: background-color .2s ease, border .2s ease;

			.step-circle-inner {
				@include centered;
				background-color: var(--color-bg);
				width: 22px;
				height: 22px;
				border-radius: 50%;
				transition: background-color .2s ease;

				.circle-check {
					@include centered;
					display: none;

					&:deep(svg) {
						height: 10px;
						width: 10px;

						.stroke {
							stroke: var(--color-bg);
						}

						.fill {
							fill: var(--color-bg);
						}
					}
					&.ai-circle-icon, &.warn-icon {
						&:deep(svg) {
							height: 14px;
							width: 14px;
						}
					}
				}
				.flag-icon {
					@include centered;
					display: flex;

					&:deep(svg) {
						height: 12px;
						width: 12px;
						
						.stroke { stroke: var(--color-alpha-2); }
						.fill { fill: var(--color-alpha-2); }
					}
				}
			}

		}

		&.active {
			.stepper-step-header {
				color: var(--color-primary);
				cursor: pointer;
			}
			.step-circle {
				background-color: var(--color-bg);
				border: 1px solid var(--color-primary);

				.step-circle-inner {
					background-color: var(--color-primary);
					.flag-icon {
						&:deep(svg) {
							.stroke { stroke: var(--color-bg); }
							.fill { fill: var(--color-bg); }
						}
					}
				}
			}
			.stepper-step-content .stepper-step-line-container .stepper-step-line {
				background-color: var(--color-primary);
			}
		}
		&.done {
			.stepper-step-header {
				color: #0C9A72;
				cursor: pointer;
			}
			.step-circle {
				background-color: var(--color-bg);
				border: 1px solid #0C9A72;

				.step-circle-inner {
					background-color: #0C9A72;
					.flag-icon {
						&:deep(svg) {
							.stroke { stroke: var(--color-bg); }
							.fill { fill: var(--color-bg); }
						}
					}
				}
				.circle-check {
					display: flex;
				}
			}
			.stepper-step-content .stepper-step-line-container .stepper-step-line {
				background-color: #0C9A72;
			}
		}
		&.failed {
			.stepper-step-header {
				color: var(--color-danger);
			}
			.step-circle {
				background-color: var(--color-bg);
				border: 1px solid var(--color-danger);

				.step-circle-inner {
					background-color: var(--color-danger);
				}
				.circle-check {
					display: flex;
				}
			}
			.stepper-step-content .stepper-step-line-container .stepper-step-line {
				background-color: var(--color-danger);
			}
		}
		&.loading {
			.stepper-step-header {
				color: #8F4DFE;
			}
			.step-circle {
				border: 1px solid #8F4DFE;

				.step-circle-inner {
					background-color: #8F4DFE;
				}
				.circle-check {
					display: flex;
				}
			}
			.stepper-step-content .stepper-step-line-container .stepper-step-line {
				background-color: #8F4DFE;
			}
		}
		&.no-pointer {
			.stepper-step-header {
				cursor: default;
			}
		}

		@media (max-width: 480px) {
			.stepper-step-header { gap: 6px; }
			.stepper-step-content {
				gap: 6px;
				.stepper-step-dropdown-container { width: calc(100% - 40px) }
			}
		}

		@media (max-width: 360px) {
			.stepper-step-content {
				.stepper-step-line-container { width: 12px; }
				.stepper-step-dropdown-container { width: calc(100% - 12px) }
			}
		}

	}
}
</style>